"use client";

import * as React from "react";
import InputMask from "react-input-mask";
import { CalendarIcon } from "@radix-ui/react-icons";
import { format, parse } from "date-fns";
import { isMatch } from "react-day-picker"; // ✅ helper to check matcher objects

import { cn } from "../lib/utils";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Calendar } from "./ui/calendar";
import { Popover, PopoverContent, PopoverTrigger } from "./ui/popover";

export function DateInputPicker({
  date,
  onSelect,
  className = "",
  inputClassName = "",
  disabledDate = undefined, // matcher object e.g. { after: new Date() }
  futureYear = "",
  handleDateInputChange,
}) {
  const [open, setOpen] = React.useState(false);
  const [inputValue, setInputValue] = React.useState(
    date ? format(date, "dd-MM-yyyy") : "",
  );
  const [calendarMonth, setCalendarMonth] = React.useState(undefined);

  React.useEffect(() => {
    if (date) {
      setInputValue(format(date, "dd-MM-yyyy"));
      setCalendarMonth(date); // sync month when date changes
    }
  }, [date]);

  // ✅ check if a date matches the disabledDate matcher
  const isDateDisabled = (d) => {
    if (!d || !disabledDate) return false;
    return isMatch(d, disabledDate);
  };

  const handleSelect = (selectedDate) => {
    if (selectedDate) {
      // if disabled → fallback to today
      const finalDate = isDateDisabled(selectedDate)
        ? new Date()
        : selectedDate;
      onSelect(finalDate);
      setInputValue(format(finalDate, "dd-MM-yyyy"));
      setCalendarMonth(finalDate);
      setOpen(false);
    }
  };

  const handleInputChange = (e) => {
    setInputValue(e.target.value);
  };

  const handleInputBlur = () => {
    if (inputValue && inputValue.includes("_") === false) {
      const parsed = parse(inputValue, "dd-MM-yyyy", new Date());
      if (!isNaN(parsed)) {
        const finalDate = isDateDisabled(parsed) ? new Date() : parsed;
        onSelect(finalDate);
        handleDateInputChange &&
          handleDateInputChange(finalDate ? finalDate : "");
        setInputValue(format(finalDate, "dd-MM-yyyy"));
        setCalendarMonth(finalDate);
      } else {
        handleDateInputChange && handleDateInputChange(date ? date : "");
        setInputValue(date ? format(date, "dd-MM-yyyy") : "");
      }
    } else {
      handleDateInputChange && handleDateInputChange("");
    }
  };

  return (
    <div className={cn("relative", className)}>
      {/* Masked Input */}
      <InputMask
        mask="99-99-9999"
        value={inputValue}
        onChange={handleInputChange}
        onBlur={handleInputBlur}
      >
        {(inputProps) => (
          <Input
            {...inputProps}
            placeholder="dd-mm-yyyy"
            className={cn("h-9 w-[125px]", inputClassName)}
          />
        )}
      </InputMask>

      {/* Calendar Popover Trigger */}
      <Popover open={open} onOpenChange={setOpen}>
        <PopoverTrigger asChild>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            onClick={() => setOpen(!open)}
            className="absolute top-0 right-0 focus-visible:ring-webPrimary dark:focus-visible:ring-ring hover:text-webPrimary text-webPrimary border-l rounded-ss-none rounded-es-none h-9"
          >
            <CalendarIcon className="h-4 w-4" />
          </Button>
        </PopoverTrigger>
        <PopoverContent className="w-auto p-0" align="center">
          <Calendar
            mode="single"
            selected={date}
            month={calendarMonth}
            onMonthChange={setCalendarMonth}
            onSelect={handleSelect}
            initialFocus
            disabled={disabledDate}
            toYear={futureYear}
          />
        </PopoverContent>
      </Popover>
    </div>
  );
}
